import { useEffect, useState } from "react";
import api, { money, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock, ArrowUp, ArrowDown } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";

const EMPTY = { name: "", price: 0, duration: 30, description: "", image: "", active: true };

export default function Services() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/services", { params: { all: true } }).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm(s); setOpen(true); };

  const save = async () => {
    const payload = { ...form, price: Number(form.price), duration: Number(form.duration) };
    try {
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success("Serviço salvo. A agenda foi atualizada automaticamente.");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/services/${id}`); toast.success("Removido"); load(); } catch { toast.error("Erro"); } };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try { await api.post("/services/reorder", { ids: arr.map((s) => s.id) }); }
    catch { toast.error("Erro ao reordenar"); load(); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Serviços</h1><p className="text-zinc-400 mt-1">A duração define a disponibilidade da agenda.</p></div>
        <Button className="bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full" onClick={openNew} data-testid="add-service-btn"><Plus className="w-4 h-4 mr-1" /> Novo</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s, idx) => (
          <div key={s.id} className={`bg-zinc-900 border rounded-2xl overflow-hidden ${s.active ? "border-zinc-800" : "border-zinc-800 opacity-60"}`} data-testid={`service-item-${s.id}`}>
            {s.image && <div className="h-32 overflow-hidden"><img src={s.image} alt={s.name} className="w-full h-full object-cover" /></div>}
            <div className="p-5">
              <div className="flex items-start justify-between">
                <h3 className="font-heading font-bold text-white">{s.name}</h3>
                <span className="text-amber-400 font-bold">{money(s.price)}</span>
              </div>
              <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{s.description}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm text-zinc-500 flex items-center gap-1"><Clock className="w-4 h-4" /> {s.duration} min</span>
                {!s.active && <span className="text-xs text-red-400">Inativo</span>}
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="icon" variant="outline" className="border-zinc-700 text-zinc-400 h-9 w-9 shrink-0" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`service-up-${s.id}`}><ArrowUp className="w-4 h-4" /></Button>
                <Button size="icon" variant="outline" className="border-zinc-700 text-zinc-400 h-9 w-9 shrink-0" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} data-testid={`service-down-${s.id}`}><ArrowDown className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" className="flex-1 border-zinc-700 text-zinc-300" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}><Pencil className="w-4 h-4 mr-1" /> Editar</Button>
                <Button size="sm" variant="outline" className="border-red-500/40 text-red-400" onClick={() => remove(s.id)} data-testid={`delete-service-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editing ? "Editar" : "Novo"} Serviço</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-300">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="service-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-zinc-300">Preço (R$)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="service-price" /></div>
              <div><Label className="text-zinc-300">Duração (min)</Label><Input type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="service-duration" /></div>
            </div>
            <div><Label className="text-zinc-300">Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="service-description" /></div>
            <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} aspect={4 / 3} label="Foto do serviço" testid="service-image" previewClass="h-40" />
            <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="service-active" /><Label className="text-zinc-300">Ativo</Label></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-amber-400 text-black font-bold" data-testid="save-service-btn">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
